// @flow

import type {EventRule, EventRuleChange, EventSubRule} from '@/types'

import {
  call, chain, concat, defaultTo, descend, evolve, find, groupBy, head, length,
  map, mergeAll, pick, pipe, pluck, prop, propEq, sort, values, zipWith,
} from 'ramda'
import {toIdMapBy} from '@/store/util'
import {globalRulesAlert, controlloingOwnershipRuleCodes} from './defaults'
import {prependRulesIndexerString} from '@/store/portfolios/util'

// New SubRule groups object creation //
const orderRulesByOrderIndex = (rules) => {
  const groupArrayByOrderIndex = []
  let groupByOrderIndex = groupBy(prop('orderIndex'))
  map((order) => {
    map((groups) => {
      groupArrayByOrderIndex.push(groups)
    }, order)
  }, values(groupByOrderIndex(rules)))
  return groupArrayByOrderIndex
}
const formSubRulesObjects = (subRules) => {
  const totalSubRules = {}
  if (subRules && subRules.notificationRules.length > -1) {
    const subRulesByOrderIndex = orderRulesByOrderIndex(subRules.notificationRules)
    for (let i = 0; i < subRulesByOrderIndex.length; i++) {
      const id = (subRulesByOrderIndex: Array<any>)[i]['ruleCode']
      const indexer = prependRulesIndexerString('rule', id)
      totalSubRules[indexer] = subRulesByOrderIndex[i]
    }
    return totalSubRules
  }
}
const formTotalRulesObject = (subRules) => {
  let totalSubRulesObj = []
  if (subRules && subRules.length > -1) {
    for (let i = 0; i < subRules.length; i++) {
      let groupObjects = {
        groupId: subRules[i].groupId,
        allRuleCode: subRules[i].allRuleCode,
        name: subRules[i].name,
        orderIndex: subRules[i].orderIndex,
        subGroupRules: formSubRulesObjects(subRules[i]),
      }
      totalSubRulesObj.push(groupObjects)
    }
  }
  return totalSubRulesObj
}
const formTotalNotificationRules = (notificationRules) => {
  const totalRulesObj = {}
  notificationRules = orderRulesByOrderIndex(notificationRules)
  for (let i = 0; i < notificationRules.length; i++) {
    const id = (notificationRules: Array<any>)[i]['ruleCode']
    const indexer = prependRulesIndexerString('rule', id)
    totalRulesObj[indexer] = notificationRules[i]
  }
  return totalRulesObj
}

const formRuleObject = (rules) => {
  return {
    groupInfo: rules,
    rules: rules,
    notificationRules: formTotalNotificationRules(rules.notificationRules) || {},
    subGroupInfo: rules.subGroups,
    subGroupRules: formTotalRulesObject(rules.subGroups),
  }
}
const formGroupObject = (groups) => {
  const groupObj = {}
  groups = orderRulesByOrderIndex(groups)
  for (let i = 0; i < groups.length; i++) {
    const id = (groups: Array<any>)[i]['groupId']
    const indexer = prependRulesIndexerString('group', id)
    groupObj[indexer] = formRuleObject(groups[i])
  }
  return groupObj
}
export const formRules = (key: any) => {
  const rulesObj = {}
  for (let i = 0; i < key.length; i++) {
    rulesObj[prop('countryCode', key[i])] = formGroupObject(prop('groups', key[i]))
  }

  return rulesObj
}
// End of adding SubGroups //

const byGroup = groupBy(prop('groupId'))

const byLength = sort(descend(length))

const byCountry = (name) => groupBy(prop(name))

export const orderGroups = pipe(
  values,
  byGroup,
  values,
  byLength,
)

const collect = (countryCodeProp: string) => pipe(
  byCountry(countryCodeProp),
  map(byGroup),
)
const collectRules = collect('ruleCountryCode')
const collectGroups = collect('countryCode')

const combine = (rulesGroup) => (rules) => ({
  groupInfo: head(rulesGroup),
  rules: toIdMapBy('ruleCode')(rules),
})

const toGroupInfoAdder = map(map(combine))

const addGroupInfoToGroups = ([rules, ruleGroups]) =>
  evolve(toGroupInfoAdder(ruleGroups), rules)

export const combineRuleInfo = pipe(
  zipWith(call, [collectRules, collectGroups]),
  addGroupInfoToGroups,
)

const CHANGE_FIELDS = [
  'ruleCode',
  'ruleCountryCode',
  'isActive',
  'param0',
  'param1',
  'param2',
]
const getRules = pipe(prop('notificationRules'), values)
const getSubRules = pipe(prop('subGroupRules'), values)

const getSubRuleChanges = pipe(
  prop('subGroupRules'),
  values,
  chain(getSubRules),
  map(pick(CHANGE_FIELDS)),
)
// EventRulesCountry -> Array<EventRuleChange>

export const combineRuleChanges = (ruleChanges: Array<any>, subRuleChanges: Array<any>) => {
  let combinedRules = []
  for (let i = 0; i < ruleChanges.length && i < subRuleChanges.length; i++) {
    if (ruleChanges[i][0] === subRuleChanges[i][0]) {
      let concatRules = []
      concatRules.push(ruleChanges[i][0])
      concatRules.push(concat(ruleChanges[i][1], subRuleChanges[i][1]))
      combinedRules.push(concatRules)
    }
  }
  return combinedRules
}
export const toRuleChanges = pipe(
  values,
  chain(getRules),
  map(pick(CHANGE_FIELDS)),
)

export const toSubRuleChanges = pipe(
  values,
  chain(getSubRuleChanges),
)
export const isActive = (ruleCode: number) => pipe(
  values,
  chain(values),
  pluck('rules'),
  mergeAll,
  (rulesByCode) => rulesByCode[ruleCode].isActive === 1,
)

export const toggleActive = (rule: EventRule): number => rule.isActive ? 0 : 1

const active = (propName: string) => (code: number) => pipe(
  find(propEq('ruleCode', code)),
  defaultTo({}),
  propEq(propName, 1),
)

export const activeInRules = active('isActive')
export const activeInChanges = active('val')

const baseRule = (onOff: number) => (code: number) => ({
  ruleCode: code,
  prop: 'isActive',
  val: onOff,
})

export const ruleOff = baseRule(0)
export const ruleOn = baseRule(1)

const activateAllExceptAny = (allCode, anyCode, rules) => {
  const f = (r) =>
    r.ruleCode === anyCode
      ? ruleOff(r.ruleCode)
      : ruleOn(r.ruleCode)

  return map(f, rules)
}

const deactivateAll = (allCode, anyCode, rules) => {
  const f = pipe(
    prop('ruleCode'),
    ruleOff,
  )

  return map(f, rules)
}

export const toggleAllWhere = (
  allCode: number,
  anyCode: ?number,
) => (
  rules: Array<EventRule>,
): Array<EventRuleChange> =>
  activeInRules(allCode)(rules)
    ? deactivateAll(allCode, anyCode, rules)
    : activateAllExceptAny(allCode, anyCode, rules)

export const toggleSubAllWhere = (
  // Toggle all rule code in Sub rules
  allCode: number,
  anyCode: ?number,
) => (
  rules: Array<EventSubRule>,
): Array<EventRuleChange> =>
  activeInRules(allCode)(rules)
    ? deactivateAll(allCode, anyCode, rules)
    : activateAllExceptAny(allCode, anyCode, rules)

const deactivateAny = (allCode, anyCode, rules) => {
  return [ruleOff(anyCode)]
}

const activateAny = (allCode, anyCode, rules) => {
  const f = (r) =>
    r.ruleCode === anyCode
      ? ruleOn(r.ruleCode)
      : ruleOff(r.ruleCode)

  return map(f, rules)
}

export const toggleAnyWhere = (
  allCode: number,
  anyCode: number,
) => (
  rules: Array<EventRule>,
): Array<EventRuleChange> =>
  activeInRules(anyCode)(rules)
    ? deactivateAny(allCode, anyCode, rules)
    : activateAny(allCode, anyCode, rules)

export const tokenizeRuleName = (ruleName: string): Array<string> => {
  const tokens = []
  let token = ''
  let i = 0

  const storeToken = () => {
    tokens.push(token)
    token = ''
  }

  const skip = () => {
    i += 1
  }

  while (i <= ruleName.length) {
    if (ruleName[i] === '|') {
      storeToken()
      tokens.push('|')
      skip()
    } else if (ruleName[i] === '{') {
      storeToken()
    } else if (ruleName[i] === '}') {
      token += '}'
      storeToken()
      skip()
    } else if (i === ruleName.length) {
      storeToken()
    }

    token += ruleName[i]
    i += 1
  }

  return tokens
}

export const getTranslatedTooltip = (lang: string, l: (text: string) => void, data: Array<string>) => {
  return data.map((text) => {
    let translatedText = l(text) || text
    if (lang && lang.toUpperCase() !== 'GB' && text === translatedText) {
      return ''
    }
    return translatedText + '.'
  })
}

export const getAlertByRuleCode = (ruleCode: string, portfolioCountries: Array<string>) => {
  const alertInfo = globalRulesAlert[ruleCode]
  if (alertInfo) {
    const isAnyCountryExists = find((country) => alertInfo.countryList.indexOf(country) > -1,
      portfolioCountries)
    return isAnyCountryExists || controlloingOwnershipRuleCodes.indexOf(ruleCode.replace('rule', '')) > -1 ? alertInfo.alert : ''
  }
  return ''
}
export const mergerActiveCount = (checked: number, ruleCode: number) => {
  return (checked === 1 && (ruleCode === 510 || ruleCode > 528))
}



// WEBPACK FOOTER //
// ./src/rules-helper.js